import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { PageHeader, ScoreBadge, EmailFunnel } from "@/components/Common";
import AutopilotQueue from "@/components/AutopilotQueue";
import AIDiscoveryDialog from "@/components/AIDiscoveryDialog";
import { FinderOptionsDialog } from "@/components/FinderOptionsDialog";
import { ImportContactsDialog } from "@/components/ImportContactsDialog";
import { TranslateControls } from "@/components/TranslateControls";
import { SortSelect, useSortOptions, sortRows, pickRecentId } from "@/components/SortSelect";
import ContactNowBanner from "@/components/ContactNowBanner";
import { EmailPreviewButton } from "@/components/EmailPreviewDialog";
import { EmailComposer, BatchCcBcc } from "@/components/EmailComposer";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Newspaper, Loader2, Sparkles, Globe, Users, Mail, Copy, Check, Send, FileText,
  Trash2, Archive, Inbox, Save, Paperclip, X, Megaphone, FilePlus2, Eye, Languages,
  BarChart3, TrendingUp, MailOpen, MousePointerClick, Reply, CheckCircle2, Plus, Target, Settings2,
  AlertTriangle, Link2, Layers, FileSpreadsheet, Pencil, Gauge, RotateCcw, MessageCircle, Phone,
} from "lucide-react";
import { MediaEditDialog } from "@/components/MediaEditDialog";
import { MessageBody } from "@/components/MessageBody";
import { BulkCampaignComposer, ChannelToggle, contactWaNumber } from "@/components/BulkCampaignComposer";

const fmtShortDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" }); }
  catch { return ""; }
};

const CATEGORIES = ["Bike Tecnico", "MTB / Off Road Bike", "Gravel/Outdoor", "Lifestyle & Moda"];
const NATIONS = ["Italia", "Germania", "Francia", "Spagna", "Regno Unito", "Stati Uniti", "Internazionale"];
const LANGUAGES = ["Italiano", "Inglese", "Tedesco", "Francese", "Spagnolo"];
const STATUSES = ["nuovo", "contattato", "interessato", "salvato", "non contattare", "scartato"];
const QUALITY_OPTIONS = [
  { value: "0", label: "Tutti i match" },
  { value: "70", label: "Match ≥ 70%" },
  { value: "80", label: "Match ≥ 80%" },
  { value: "90", label: "Match ≥ 90%" },
];
const CHUNK = 4 * 1024 * 1024; // 4MB

const DELIVERY_LABELS = { inviata: "Inviata", consegnata: "Consegnata", aperta: "Aperta", click: "Cliccata", risposta: "Risposta", rimbalzata: "Rimbalzata", reclamo: "Reclamo", "in ritardo": "In ritardo" };
const deliveryClass = (s) => {
  if (s === "risposta") return "bg-emerald-500/20 text-emerald-800 dark:text-emerald-200 border border-emerald-500/40";
  if (["aperta", "click"].includes(s)) return "bg-green-500/20 text-green-800 dark:text-green-200 border border-green-500/40";
  if (["rimbalzata", "reclamo"].includes(s)) return "bg-red-500/20 text-red-800 dark:text-red-200 border border-red-500/40";
  if (s === "consegnata") return "bg-sky-500/20 text-sky-800 dark:text-sky-200 border border-sky-500/40";
  if (s === "in ritardo") return "bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40";
  return "bg-slate-500/20 text-slate-800 dark:text-slate-200 border border-slate-500/40";
};
const batchStatusClass = (s) => {
  if (s === "inviata") return "bg-green-500/20 text-green-800 dark:text-green-200 border border-green-500/40";
  if (s === "parziale") return "bg-amber-500/20 text-amber-900 dark:text-amber-200 border border-amber-500/40";
  return "bg-slate-500/20 text-slate-800 dark:text-slate-200 border border-slate-500/40";
};
const batchStatusLabel = (s) => (s === "inviata" ? "Inviata" : s === "parziale" ? "Parziale" : "Bozza");
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

async function uploadAttachment(file) {
  const { data: initData } = await api.post("/uploads/init");
  const uploadId = initData.upload_id;
  const total = file.size;
  for (let start = 0; start < total; start += CHUNK) {
    const blob = file.slice(start, Math.min(start + CHUNK, total));
    await api.put(`/uploads/${uploadId}/chunk`, blob, { headers: { "Content-Type": "application/octet-stream" } });
  }
  const { data } = await api.post("/attachments/finalize", {
    upload_id: uploadId, filename: file.name, content_type: file.type || "",
  });
  return data.attachment;
}

function AttachmentUploader({ attachments, onChange, testidPrefix = "att" }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of files) added.push(await uploadAttachment(f));
      await onChange([...(attachments || []), ...added]);
      toast.success(`${added.length} allegato/i aggiunto/i`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload allegato fallito");
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = "";
    }
  };
  const remove = async (sp) => onChange((attachments || []).filter((a) => a.storage_path !== sp));
  return (
    <div className="space-y-2">
      <input ref={ref} type="file" multiple hidden onChange={handleFiles} data-testid={`${testidPrefix}-file-input`} />
      <Button type="button" variant="secondary" size="sm" disabled={uploading} onClick={() => ref.current?.click()}
        data-testid={`${testidPrefix}-add-btn`}
        className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
        {uploading ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Caricamento…</> : <><Paperclip className="mr-1.5 h-3.5 w-3.5" /> Aggiungi allegato</>}
      </Button>
      {(attachments || []).length > 0 && (
        <ul className="space-y-1">
          {attachments.map((a) => (
            <li key={a.storage_path} data-testid={`${testidPrefix}-item`}
              className="flex items-center justify-between gap-2 rounded-lg bg-brema-bg px-3 py-1.5 text-xs">
              <a href={fileUrl(a.storage_path)} target="_blank" rel="noopener noreferrer"
                className="flex min-w-0 items-center gap-1.5 truncate text-foreground hover:text-brand hover:underline">
                <Paperclip className="h-3 w-3 shrink-0 text-brand" />
                <span className="truncate">{a.filename}</span>
                {a.size_label && <span className="shrink-0 text-muted-foreground">· {a.size_label}</span>}
              </a>
              <button type="button" onClick={() => remove(a.storage_path)} data-testid={`${testidPrefix}-remove-btn`}
                className="shrink-0 text-muted-foreground hover:text-danger"><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Read-only attachment chips shown inside each draft + history detail
function AttachmentChips({ attachments, onRemove, testid = "draft-att-chip" }) {
  if (!(attachments || []).length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="draft-attachments">
      {attachments.map((a) => (
        <span key={a.storage_path} data-testid={testid}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-brema-bg px-2 py-1 text-[11px] text-foreground">
          <Paperclip className="h-3 w-3 text-brand" />
          <a href={fileUrl(a.storage_path)} target="_blank" rel="noopener noreferrer"
            className="max-w-[160px] truncate hover:text-brand hover:underline">Allegato: {a.filename}</a>
          <a href={fileUrl(a.storage_path)} target="_blank" rel="noopener noreferrer" title="Visualizza"
            className="text-muted-foreground hover:text-brand"><Eye className="h-3 w-3" /></a>
          {onRemove && (
            <button type="button" onClick={() => onRemove(a.storage_path)} title="Rimuovi"
              data-testid="draft-att-remove-btn" className="text-muted-foreground hover:text-danger"><X className="h-3 w-3" /></button>
          )}
        </span>
      ))}
    </div>
  );
}

function MediaCard({ m, i, checked, onToggle, onStatus, onPitch, onEdit }) {
  return (
    <motion.div
      key={m?._id || i}
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
      data-testid="media-card"
      className="flex flex-col rounded-xl border border-border bg-brema-card p-5"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Checkbox checked={checked} onCheckedChange={() => onToggle(m?._id)}
            data-testid={`media-select-checkbox-${m?._id}`} className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
          <div>
            <h4 className="font-semibold text-foreground">{m?.name || "Testata"}</h4>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-brema-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand">{m?.outlet_type || "Sito"}</span>
              <span className="text-xs text-muted-foreground">{m?.category || "N/D"}</span>
              {m?.nation && <span className="flex items-center gap-0.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-foreground" data-testid="media-nation-badge"><Globe className="h-2.5 w-2.5" /> {m.nation}</span>}
              {m?.source === "real" && <span className="rounded-full bg-brema-navy px-1.5 py-0.5 text-[10px] font-medium text-white dark:bg-emerald-500/15 dark:text-emerald-400" data-testid="media-source-badge">Reale</span>}
              {m?.source === "simulated" && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground" data-testid="media-source-badge">Demo</span>}
              {m?.pitch_delivery_status && <span data-testid="media-pitch-badge" className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${deliveryClass(m.pitch_delivery_status)}`}>PR: {DELIVERY_LABELS[m.pitch_delivery_status] || m.pitch_delivery_status}</span>}
              {m?.status === "contattato" && <span data-testid="media-contacted-badge" className="flex items-center gap-0.5 rounded-full bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300"><CheckCircle2 className="h-2.5 w-2.5" /> Contattato{m?.pitch_sent_at ? ` · ${fmtShortDate(m.pitch_sent_at)}` : ""}</span>}
              {m?.link_status === "invalid" && <span data-testid="media-link-invalid-badge" className="flex items-center gap-0.5 rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400"><AlertTriangle className="h-2.5 w-2.5" /> Link non valido{m?.link_code ? ` (${m.link_code})` : ""}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <ScoreBadge score={m?.fit_score} />
          {onEdit && (
            <button type="button" onClick={() => onEdit(m)} title="Modifica testata (URL, email, referenti)" data-testid="media-edit-btn"
              className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-brema-hover hover:text-brand"><Pencil className="h-4 w-4" /></button>
          )}
        </div>
      </div>
      <p className="mb-3 text-sm text-muted-foreground">{m?.description || "—"}</p>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-brema-bg px-3 py-2">
          <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Users className="h-3 w-3" /> Lettori/mese</p>
          <p className="text-sm font-bold text-foreground">{Number(m?.monthly_reach || 0).toLocaleString("it-IT")}</p>
        </div>
        <div className="rounded-lg bg-brema-bg px-3 py-2">
          <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground"><Globe className="h-3 w-3" /> Sito</p>
          {m?.website ? (
            <a href={/^https?:\/\//i.test(m.website) ? m.website : `https://${m.website}`} target="_blank" rel="noopener noreferrer" data-testid="media-website-link" className="block truncate text-xs text-foreground hover:text-brand hover:underline">{m.website}</a>
          ) : <p className="truncate text-xs text-foreground">N/D</p>}
        </div>
      </div>
      {m?.target_readers && <p className="mb-3 text-xs text-muted-foreground"><span className="text-muted-foreground">Target:</span> {m.target_readers}</p>}
      <div className="mb-3 space-y-1 text-xs text-muted-foreground">
        {(m?.editor_name || m?.editor_email) && (
          <p className="flex items-center gap-1.5">
            <Mail className="h-3 w-3" /> {m?.editor_name || "Redazione"}
            {m?.editor_email ? <> · <a href={`mailto:${m.editor_email}`} data-testid="media-email-link" className="text-foreground hover:text-brand hover:underline">{m.editor_email}</a></> : ""}
          </p>
        )}
        {(m?.phone || m?.whatsapp) && (
          <p className="flex items-center gap-1.5" data-testid="media-phone-line">
            {m?.whatsapp ? <MessageCircle className="h-3 w-3 text-[#25D366]" /> : <Phone className="h-3 w-3" />}
            <span className="text-foreground">{m?.whatsapp || m?.phone}</span>
            {m?.whatsapp && m?.phone && m.phone !== m.whatsapp ? <span>· tel. {m.phone}</span> : null}
          </p>
        )}
        {(Array.isArray(m?.journalists) ? m.journalists : []).map((j, idx) => (
          <p key={idx} className="flex items-center gap-1.5 pl-4 text-muted-foreground">
            {j?.name}{j?.role ? ` (${j.role})` : ""}{j?.email ? ` · ${j.email}` : ""}
          </p>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-1.5">
        {(Array.isArray(m?.topics) ? m.topics : []).map((t, idx) => (
          <span key={idx} className="rounded-full bg-brema-bg px-2 py-0.5 text-[11px] text-foreground">{t}</span>
        ))}
      </div>
      <div className="mt-auto flex items-center gap-2">
        <Select value={m?.status || "nuovo"} onValueChange={(v) => onStatus(m, v)}>
          <SelectTrigger className="h-9 flex-1" data-testid="media-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" data-testid="pitch-btn" onClick={() => onPitch(m)}
          className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
          <Sparkles className="mr-1 h-3.5 w-3.5" /> Pitch
        </Button>
      </div>
    </motion.div>
  );
}

export default function MediaFinder() {
  // --- Discovery ---
  const [category, setCategory] = useState("Bike Tecnico");
  const [categories, setCategories] = useState(CATEGORIES);
  const [catMgr, setCatMgr] = useState(false);
  const loadCategories = () => api.get("/admin/finder-options?group=media_category").then(({ data }) => { if (data.options?.length) setCategories(data.options.map((o) => o.value)); }).catch(() => {});
  useEffect(() => { loadCategories(); }, []);
  const [nation, setNation] = useState("Italia");
  const [count, setCount] = useState(5);
  const [discovering, setDiscovering] = useState(false);
  const [discoverProgress, setDiscoverProgress] = useState(null);
  const [integrations, setIntegrations] = useState(null);

  // --- Archive / list ---
  const [list, setList] = useState([]);
  const [filterCategory, setFilterCategory] = useState("tutte");
  const [filterNation, setFilterNation] = useState("tutte");
  const [minScore, setMinScore] = useState("0");
  const [filterStatus, setFilterStatus] = useState("tutti");
  const [mediaSort, setMediaSort] = useState("");
  const [resultsSort, setResultsSort] = useState("");
  const [savingResults, setSavingResults] = useState(false);
  const { options: sortOptions, catalog: sortCatalog } = useSortOptions("redazionali");
  const [tab, setTab] = useState("testate");

  // --- Selection / bulk ---
  const [selected, setSelected] = useState({});
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [justArchived, setJustArchived] = useState([]);
  const [bulkNews, setBulkNews] = useState("");
  const [bulkPitchType, setBulkPitchType] = useState("email");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkChannel, setBulkChannel] = useState("email");
  const [bulkComposerOpen, setBulkComposerOpen] = useState(false);

  // --- Quick pitch dialog ---
  const [dialogMedia, setDialogMedia] = useState(null);
  const [pitchType, setPitchType] = useState("email");
  const [news, setNews] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { subject, body }
  const [copied, setCopied] = useState(false);
  const [pitchAttachments, setPitchAttachments] = useState([]);
  const [sending, setSending] = useState(false);

  // --- Master compose dialog ---
  const [masterOpen, setMasterOpen] = useState(false);
  const [masterType, setMasterType] = useState("press_release");
  const [masterLang, setMasterLang] = useState("Italiano");
  const [masterNews, setMasterNews] = useState("");
  const [masterSubject, setMasterSubject] = useState("");
  const [masterBody, setMasterBody] = useState("");
  const [masterAtts, setMasterAtts] = useState([]);
  const [masterGenerating, setMasterGenerating] = useState(false);
  const [masterSaving, setMasterSaving] = useState(false);

  // --- Campaigns / batches ---
  const [batches, setBatches] = useState([]);
  const [activeBatch, setActiveBatch] = useState(null);
  const [batchMessages, setBatchMessages] = useState([]);
  const [batchAttachments, setBatchAttachments] = useState([]);
  const [prTemplates, setPrTemplates] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchSending, setBatchSending] = useState(false);
  const [msgSel, setMsgSel] = useState({});
  const [recipientSel, setRecipientSel] = useState({});
  const [addingRecipients, setAddingRecipients] = useState(false);

  // --- History ---
  const [history, setHistory] = useState([]);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [historySel, setHistorySel] = useState(new Set());
  const [deletingHist, setDeletingHist] = useState(false);
  const [histDelOpen, setHistDelOpen] = useState(false);
  const toggleHistSel = (id) => setHistorySel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleHistAll = () => setHistorySel((s) => (s.size === history.length ? new Set() : new Set(history.map((m) => m._id))));
  const deleteHistorySelected = async () => {
    const ids = [...historySel];
    if (!ids.length) return;
    setDeletingHist(true);
    try {
      const { data } = await api.post("/campaigns/history/delete-selected", { ids });
      toast.success(`${data.deleted} invii eliminati dallo storico`);
      if (historyDetail && ids.includes(historyDetail._id)) setHistoryDetail(null);
      setHistorySel(new Set());
      setHistDelOpen(false);
      await loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita"); }
    finally { setDeletingHist(false); }
  };
  const [pipeline, setPipeline] = useState(null);
  const [pipeSel, setPipeSel] = useState(new Set());
  const [pipeRemove, setPipeRemove] = useState(null); // { ids: [], mode: "selected" | "simulated" }
  const [pipeDeleteHistory, setPipeDeleteHistory] = useState(false);
  const [pipeWorking, setPipeWorking] = useState(false);
  const togglePipeSel = (id) => setPipeSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const confirmPipeRemove = async () => {
    if (!pipeRemove) return;
    setPipeWorking(true);
    try {
      const url = pipeRemove.mode === "simulated" ? "/media/pipeline/reset-simulated" : "/media/pipeline/remove";
      const { data } = await api.post(url, { media_ids: pipeRemove.ids, delete_history: pipeDeleteHistory });
      toast.success(`${data.reset} ${data.reset === 1 ? "record rimosso" : "record rimossi"} dalla Pipeline${pipeDeleteHistory ? ` · ${data.history_deleted} invii eliminati dallo Storico` : ""}`);
      setPipeRemove(null); setPipeSel(new Set()); setPipeDeleteHistory(false);
      loadPipeline(); loadHistory(); loadMedia();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione fallita"); }
    finally { setPipeWorking(false); }
  };

  // --- Modifica scheda testata ---
  const [editMedia, setEditMedia] = useState(null);
  const onMediaSaved = (upd) => {
    setList((prev) => prev.map((x) => (x._id === upd._id ? { ...x, ...upd } : x)));
    setBatchMessages((prev) => prev.map((m) => (m.media_id === upd._id && m.status !== "inviata" && (!m.recipient || m.recipient === editMedia?.editor_email)
      ? { ...m, recipient: upd.editor_email || m.recipient, media_name: upd.name || m.media_name } : m)));
  };

  // --- Manual outlet addition ---
  const emptyManualMedia = { name: "", editor_email: "", editor_name: "", phone: "", whatsapp: "", category: "Bike Tecnico", nation: "Italia", website: "", notes: "" };
  const [manualOpen, setManualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [checkingLinks, setCheckingLinks] = useState(false);
  const [dedupingMedia, setDedupingMedia] = useState(false);
  const [manualSaving, setManualSaving] = useState(false);
  const [manualMedia, setManualMedia] = useState(emptyManualMedia);
  const setMM = (patch) => setManualMedia((p) => ({ ...p, ...patch }));
  const [lookupName, setLookupName] = useState("");
  const [lookingUp, setLookingUp] = useState(false);

  const loadMedia = () => api.get("/media").then(({ data }) => setList(data.media || [])).catch(() => {});
  const loadBatches = () => api.get("/campaign-batches?target=media").then(({ data }) => setBatches(data.batches || [])).catch(() => {});
  const loadHistory = () => api.get("/campaigns/history?target=media").then(({ data }) => setHistory(data.history || [])).catch(() => {});
  const loadPipeline = () => api.get("/media/pipeline").then(({ data }) => setPipeline(data)).catch(() => {});

  useEffect(() => {
    loadMedia(); loadBatches(); loadHistory(); loadPipeline();
    api.get("/integrations/status").then(({ data }) => setIntegrations(data)).catch(() => {});
  }, []);

  useEffect(() => { if (tab === "pipeline") loadPipeline(); }, [tab]);

  const pollDiscoverJob = (jobId) => new Promise((resolve, reject) => {
    let tries = 0, previewLoaded = false;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/discover-jobs/${jobId}`);
        const j = data.job;
        if (j.progress) {
          setDiscoverProgress(j.progress);
          if (j.progress.preview_ready && !previewLoaded) { previewLoaded = true; loadMedia(); }
        }
        if (j.status === "ready") { clearInterval(iv); resolve(j.result || {}); }
        else if (j.status === "error") { clearInterval(iv); reject(new Error(j.error || "Ricerca fallita")); }
        else if (tries > 130) { clearInterval(iv); reject(new Error("La ricerca sta impiegando troppo tempo. Riprova.")); }
      } catch (e) { if (tries > 130) { clearInterval(iv); reject(e); } }
    }, 2500);
  });

  const discover = async () => {
    setDiscovering(true);
    setDiscoverProgress({ phase: "starting" });
    try {
      const excludeIds = list.map((m) => m.dedup_key).filter(Boolean);
      const { data: init } = await api.post("/media/discover-async", { category, count: Number(count), nation, email_only: true, exclude_ids: excludeIds });
      const result = await pollDiscoverJob(init.job_id);
      const newCount = result.new_count ?? result.count ?? 0;
      const req = result.requested || Number(count);
      setFilterCategory(category);
      setFilterStatus("tutti");
      setTab("risultati");
      await loadMedia();
      if (newCount === 0) {
        toast.info(result.exhausted
          ? `Nessuna altra testata nuova con email in "${category}" · ${nation}. Categoria esaurita.`
          : `Nessuna nuova testata trovata in "${category}".`);
      } else {
        let msg = `+${newCount} nuove testate con email · "${category}" · ${nation}`;
        if (newCount < req) msg += result.exhausted ? ` · categoria esaurita (${newCount}/${req})` : ` (${newCount}/${req} richieste)`;
        toast.success(msg);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setDiscovering(false); setDiscoverProgress(null); }
  };

  const clearMedia = async () => {
    const scope = filterCategory !== "tutte" ? filterCategory : "tutte le categorie";
    if (!window.confirm(`Vuoi svuotare la lista media di ${scope}? L'azione è irreversibile.`)) return;
    try {
      const q = filterCategory !== "tutte" ? `?category=${encodeURIComponent(filterCategory)}` : "";
      const { data } = await api.delete(`/media${q}`);
      toast.success(`${data.deleted} testate rimosse`);
      setSelected({});
      loadMedia();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const checkMediaLinks = async () => {
    setCheckingLinks(true);
    try {
      const { data } = await api.post("/admin/media/check-links");
      toast.success(`Link verificati: ${data.checked} · validi ${data.valid} · non validi ${data.invalid}`);
      loadMedia();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Verifica link fallita"); }
    finally { setCheckingLinks(false); }
  };

  const dedupMedia = async () => {
    if (!window.confirm("Unire i duplicati delle testate (stesso dominio/nome)? I dati vengono uniti nel record più completo.")) return;
    setDedupingMedia(true);
    try {
      const { data } = await api.post("/admin/media/dedup");
      toast.success(data.merged > 0 ? `${data.merged} duplicati uniti` : "Nessun duplicato trovato");
      loadMedia();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Deduplica fallita"); }
    finally { setDedupingMedia(false); }
  };

  const [cleaningFalse, setCleaningFalse] = useState(false);
  const cleanFalseSends = async () => {
    if (!window.confirm("Ripristinare i pitch segnati 'Inviata' ma senza un destinatario reale? Verranno rimossi dallo storico e le testate riportate a 'nuovo'.")) return;
    setCleaningFalse(true);
    try {
      const { data } = await api.post("/admin/campaigns/clean-false-sends");
      const tot = (data.campaigns_reset || 0) + (data.media_reset || 0);
      toast.success(tot > 0 ? `Puliti ${data.campaigns_reset} invii falsi · ${data.media_reset} testate ripristinate` : "Nessun invio falso trovato");
      loadHistory(); loadMedia(); loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Pulizia fallita"); }
    finally { setCleaningFalse(false); }
  };

  const runMediaLookup = async () => {
    const q = lookupName.trim();
    if (!q) { toast.error("Digita il nome della testata da cercare"); return; }
    setLookingUp(true);
    try {
      const { data } = await api.post("/media/lookup", { name: q, nation: manualMedia.nation || "Italia", category: manualMedia.category || "Bike Tecnico" });
      const p = data.preview || {};
      setMM({ name: p.name || q, website: p.website || "", editor_email: p.editor_email || "", editor_name: p.editor_name || "", category: p.category || manualMedia.category, nation: p.nation || manualMedia.nation, notes: p.notes || "" });
      if (data.found) toast.success("Dati compilati dall'AI · controlla e salva");
      else toast("Nessun dato trovato online · compila manualmente");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLookingUp(false); }
  };

  const submitManualMedia = async () => {
    if (!manualMedia.name.trim()) { toast.error("Il nome della testata è obbligatorio"); return; }
    setManualSaving(true);
    try {
      const { data } = await api.post("/media/manual", manualMedia);
      toast.success(data.created ? "Testata aggiunta in archivio" : (data.note || "Testata già presente"));
      setManualOpen(false);
      setManualMedia(emptyManualMedia);
      setTab("testate");
      await loadMedia();
      if (data.created && data.media) setJustArchived([data.media]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setManualSaving(false); }
  };

  const changeStatus = async (m, status) => {
    setList((prev) => prev.map((x) => (x._id === m._id ? { ...x, status } : x)));
    try { await api.patch(`/media/${m._id}`, { status }); }
    catch { toast.error("Aggiornamento fallito"); loadMedia(); }
  };

  // --- quick pitch ---
  const openPitch = (m) => {
    setDialogMedia(m); setResult(null); setPitchType("email"); setNews(""); setCopied(false); setPitchAttachments([]);
  };
  const contactNext = () => {
    setJustArchived((prev) => {
      if (!prev.length) return prev;
      const [first, ...rest] = prev;
      openPitch(first);
      return rest;
    });
  };
  const generatePitch = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/media/generate-pitch", {
        media_id: dialogMedia._id, pitch_type: pitchType, news,
      });
      setResult({ subject: data.subject || "", body: data.body || "" });
      toast.success(pitchType === "email" ? "Email di pitch generata" : "Comunicato stampa generato");
      loadMedia();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setGenerating(false); }
  };
  const sendPitch = async () => {
    if (!dialogMedia || !result) return;
    setSending(true);
    try {
      const { data } = await api.post("/media/send-pitch", {
        media_id: dialogMedia._id, subject: result.subject, body: result.body,
        pitch_type: pitchType, attachments: pitchAttachments,
      });
      toast.success(data.sent ? `Email inviata a ${data.recipient}` : (data.note || "Segnata come inviata"));
      setDialogMedia(null);
      loadMedia(); loadHistory(); loadBatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito");
    } finally { setSending(false); }
  };

  // --- master compose ---
  const openMaster = () => {
    setMasterOpen(true); setMasterType("press_release"); setMasterLang("Italiano");
    setMasterNews(""); setMasterSubject(""); setMasterBody(""); setMasterAtts([]);
  };
  const generateMaster = async () => {
    setMasterGenerating(true);
    try {
      const { data } = await api.post("/media/generate-master", {
        news: masterNews, pitch_type: masterType, language: masterLang,
      });
      setMasterSubject(data.subject || "");
      setMasterBody(data.body || "");
      toast.success("Bozza generata dall'AI · rivedila prima di salvare");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setMasterGenerating(false); }
  };
  const saveMaster = async () => {
    if (!masterSubject.trim() && !masterBody.trim()) { toast.error("Scrivi almeno l'oggetto o il testo"); return; }
    setMasterSaving(true);
    try {
      const { data } = await api.post("/media/campaigns/master", {
        subject: masterSubject, body: masterBody, pitch_type: masterType,
        language: masterLang, attachments: masterAtts,
      });
      toast.success("Bozza master salvata · ora scegli i destinatari");
      setMasterOpen(false);
      setTab("campagne");
      await loadBatches();
      openBatch(data.batch);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setMasterSaving(false); }
  };

  // --- bulk generate (outlet-first) → apre subito il pop-up di composizione/invio ---
  const runBulkGenerate = async () => {
    const ids = currentRows.filter((m) => selected[m._id]).map((m) => m._id);
    if (!ids.length) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/media/campaigns/bulk-generate", { media_ids: ids, news: bulkNews, pitch_type: bulkPitchType });
      setSelected({});
      loadBatches();
      if (data.batch_id) { await openBatch({ _id: data.batch_id }); }
      const skip = data.recipients_without_email || data.skipped || 0;
      const gen = data.generated ?? ids.length;
      toast.success(`Bozze PR pronte per ${gen} testate${skip ? ` · ${skip} escluse senza email` : ""} · rivedi, scegli un modello, allega e invia`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generazione fallita");
    } finally { setBulkRunning(false); }
  };

  // --- batch detail ---
  const openBatch = async (b) => {
    setActiveBatch(b); setBatchMessages([]); setBatchAttachments([]); setBatchLoading(true);
    setMsgSel({}); setRecipientSel({});
    if (!prTemplates.length) api.get("/send-templates?module=redazionali").then(({ data }) => setPrTemplates(data.templates || [])).catch(() => {});
    try {
      const { data } = await api.get(`/campaign-batches/${b._id}`);
      setActiveBatch(data.batch);
      setBatchMessages((data.messages || []).map((m) => ({ ...m, recipient: m.status === "inviata" ? m.recipient : (m.recipient || m.outlet_email || "") })));
      setBatchAttachments(data.batch?.attachments || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBatchLoading(false); }
  };
  const applyPrTemplate = async (id) => {
    const t = prTemplates.find((x) => x._id === id);
    if (!t || !activeBatch?._id) return;
    const merged = [...batchAttachments, ...(t.attachments || []).filter((a) => a.storage_path && !batchAttachments.some((b) => b.storage_path === a.storage_path))];
    try {
      await api.post(`/campaign-batches/${activeBatch._id}/apply-template`, { subject: t.subject, body: t.body, attachments: merged });
      toast.success(`Modello "${t.title}" applicato a tutte le testate`);
      await reloadBatch();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Applicazione fallita"); }
  };
  const reloadBatch = async () => { if (activeBatch?._id) await openBatch(activeBatch); };
  const updateMsgField = (idx, field, val) =>
    setBatchMessages((prev) => prev.map((m, i) => (i === idx ? { ...m, [field]: val } : m)));
  const saveMsg = async (m) => {
    const rec = (m.recipient || "").trim();
    const bad = rec.split(/[,;]/).map((x) => x.trim()).filter(Boolean).find((x) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x));
    if (bad) { toast.error(`Email destinatario non valida: ${bad}`); return; }
    try {
      await api.patch(`/campaigns/${m._id}`, { subject: m.subject, email_body: m.email_body, recipient: rec });
      toast.success("Bozza salvata (oggetto, testo e destinatario)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const deleteMsg = async (m) => {
    if (!window.confirm(`Eliminare la bozza per "${m.media_name || m.lead_name}"?`)) return;
    try {
      await api.delete(`/campaigns/${m._id}`);
      toast.success("Bozza eliminata");
      setBatchMessages((prev) => prev.filter((x) => x._id !== m._id));
      loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const persistBatchAttachments = async (next) => {
    setBatchAttachments(next);
    if (!activeBatch?._id) return;
    try {
      await api.put(`/campaign-batches/${activeBatch._id}/attachments`, { attachments: next });
      loadBatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio allegati fallito");
    }
  };
  const addRecipients = async () => {
    const ids = Object.keys(recipientSel).filter((id) => recipientSel[id]);
    if (!ids.length || !activeBatch?._id) return;
    setAddingRecipients(true);
    try {
      const { data } = await api.post(`/campaign-batches/${activeBatch._id}/recipients`, { media_ids: ids });
      toast.success(`${data.added} destinatari aggiunti`);
      setBatchMessages((data.messages || []).map((m) => ({ ...m, recipient: m.status === "inviata" ? m.recipient : (m.recipient || m.outlet_email || "") })));
      setRecipientSel({});
      loadBatches();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setAddingRecipients(false); }
  };
  const sendMsg = async (m, idx) => {
    try {
      await api.patch(`/campaigns/${m._id}`, { subject: m.subject, email_body: m.email_body });
      const { data } = await api.post(`/campaigns/${m._id}/send`);
      toast.success(data.sent ? `Email inviata a ${m.recipient || m.media_name}` : (data.note || "Segnata come inviata"));
      updateMsgField(idx, "status", "inviata");
      loadBatches(); loadHistory(); loadMedia();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
  };
  const sendSelected = async () => {
    const ids = batchMessages.filter((m) => msgSel[m._id] && m.status !== "inviata").map((m) => m._id);
    if (!ids.length || !activeBatch?._id) return;
    if (!window.confirm(`Inviare ${ids.length} bozze selezionate?`)) return;
    setBatchSending(true);
    try {
      const { data } = await api.post(`/campaign-batches/${activeBatch._id}/send-selected`, { campaign_ids: ids });
      toast.success(`${data.sent}/${data.total} email inviate${data.errors ? ` · ${data.errors} errori` : ""}`);
      setMsgSel({});
      await reloadBatch();
      loadBatches(); loadHistory(); loadMedia();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
    finally { setBatchSending(false); }
  };
  const sendWholeBatch = async () => {
    if (!activeBatch?._id) return;
    if (!window.confirm("Inviare tutte le bozze rimanenti di questa campagna PR?")) return;
    setBatchSending(true);
    try {
      const { data } = await api.post(`/campaign-batches/${activeBatch._id}/send`);
      toast.success(`${data.sent}/${data.total} email inviate${data.errors ? ` · ${data.errors} errori` : ""}`);
      await reloadBatch();
      loadBatches(); loadHistory(); loadMedia();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio fallito"); }
    finally { setBatchSending(false); }
  };
  const deleteBatch = async (b, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm("Eliminare questa campagna PR e tutte le sue bozze?")) return;
    try {
      await api.delete(`/campaign-batches/${b._id}`);
      toast.success("Campagna eliminata");
      if (activeBatch?._id === b._id) setActiveBatch(null);
      loadBatches();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  // --- derived ---
  const showImported = () => {
    setFilterCategory("tutte"); setFilterNation("tutte"); setMinScore("0"); setFilterStatus("tutti"); setMediaSort(pickRecentId(sortOptions));
    setTab("testate");
    loadMedia();
    toast.success("Archivio Testate aggiornato: le nuove testate sono in cima alla lista");
  };
  const resultsMedia = sortRows(list.filter((m) => m?.search_staged === true), resultsSort, sortOptions, sortCatalog);
  const archiveMedia = list.filter((m) => m?.search_staged !== true);
  const visibleMedia = sortRows(archiveMedia.filter((m) => {
    if ((Number(m?.fit_score) || 0) < Number(minScore)) return false;
    if (filterCategory !== "tutte" && (m?.category || "") !== filterCategory) return false;
    if (filterNation !== "tutte" && (m?.nation || "Italia") !== filterNation) return false;
    if (filterStatus !== "tutti" && (m?.status || "nuovo") !== filterStatus) return false;
    return true;
  }), mediaSort, sortOptions, sortCatalog);
  const currentRows = tab === "risultati" ? resultsMedia : visibleMedia;
  const selectedCount = currentRows.filter((m) => selected[m._id]).length;
  const allSelected = currentRows.length > 0 && currentRows.every((m) => selected[m._id]);
  const draftBatches = batches.filter((b) => b.status !== "inviata");
  const isMaster = activeBatch?.kind === "master";
  const addedMediaIds = new Set(batchMessages.map((m) => m.media_id).filter(Boolean));
  const availableRecipients = list.filter((m) => !addedMediaIds.has(m._id));
  const recipientSelCount = Object.values(recipientSel).filter(Boolean).length;
  const draftSelCount = batchMessages.filter((m) => msgSel[m._id] && m.status !== "inviata").length;

  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const toggleSelectAll = () => {
    if (allSelected) { setSelected({}); return; }
    const next = {}; currentRows.forEach((m) => { next[m._id] = true; }); setSelected(next);
  };
  const selectOnlyWithWa = () => {
    const next = {}; visibleMedia.forEach((m) => { if (contactWaNumber(m)) next[m._id] = true; }); setSelected(next);
  };
  const bulkRecipients = archiveMedia.filter((m) => selected[m._id]);
  const waCoverage = visibleMedia.filter((m) => contactWaNumber(m)).length;

  const saveSelectedResults = async () => {
    const ids = resultsMedia.filter((m) => selected[m._id]).map((m) => m._id);
    if (!ids.length) { toast.error("Nessuna testata selezionata"); return; }
    setSavingResults(true);
    try {
      const { data } = await api.post("/media/save-selected", { media_ids: ids });
      toast.success(`${data.saved} testat${data.saved === 1 ? "a salvata" : "e salvate"} nell'Archivio Testate`, {
        action: { label: "Vedi Archivio Testate", onClick: showImported },
      });
      setSelected({});
      loadMedia();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingResults(false); }
  };

  const discardResults = async (all = false) => {
    const ids = all ? [] : resultsMedia.filter((m) => selected[m._id]).map((m) => m._id);
    if (!all && !ids.length) { toast.error("Nessuna testata selezionata"); return; }
    const n = all ? resultsMedia.length : ids.length;
    if (!window.confirm(`Rimuovere ${n} testat${n === 1 ? "a" : "e"} dai risultati di ricerca? I risultati sono temporanei: l'Archivio Testate non viene toccato.`)) return;
    try {
      const { data } = await api.post("/media/discard-results", { media_ids: ids });
      toast.success(`${data.removed} risultat${data.removed === 1 ? "o rimosso" : "i rimossi"} · Archivio Testate intatto`);
      setSelected({});
      loadMedia();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteSelectedMedia = async () => {
    const ids = visibleMedia.filter((m) => selected[m._id]).map((m) => m._id);
    if (!ids.length) { toast.error("Nessuna testata selezionata"); return; }
    if (!window.confirm(`Eliminare ${ids.length} testat${ids.length === 1 ? "a" : "e"} dall'archivio?`)) return;
    try {
      const { data } = await api.post("/media/delete-selected", { media_ids: ids });
      toast.success(`${data.deleted} testat${data.deleted === 1 ? "a eliminata" : "e eliminate"}`);
      setSelected({});
      loadMedia();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };


  const fullText = result ? `${result.subject}\n\n${result.body}` : "";

  return (
    <div>
      <PageHeader
        eyebrow="Plancia di comando Brand"
        title="Media & PR Finder"
        subtitle="Ricerca siti, blog e testate giornalistiche, prepara campagne PR con allegati e monitora gli invii."
      />

      <div className="mb-8">
        <AutopilotQueue module="redazionali" onSaved={{ reload: loadMedia, navigate: showImported }} />
      </div>

      <ContactNowBanner items={justArchived} label="testate" onContact={contactNext} onDismiss={() => setJustArchived([])} />

      {/* ===== Manual outlet addition dialog ===== */}
      <ImportContactsDialog open={importOpen} onOpenChange={setImportOpen} section="media" onDone={() => { loadMedia(); loadBatches(); }} onViewAll={showImported} />
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto border-border bg-brema-card" data-testid="manual-media-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Aggiungi testata manuale</DialogTitle>
            <DialogDescription>Inserisci una testata/redazione che già conosci. Solo il nome è obbligatorio; potrai poi generare pitch e comunicati.</DialogDescription>
          </DialogHeader>
          <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <Label className="text-xs font-semibold text-primary">✨ Importazione rapida per nome</Label>
            <p className="mb-2 mt-0.5 text-xs text-muted-foreground">Digita il nome e l'AI cerca sul web i dati (sito, email redazione, categoria). Poi controlla e salva.</p>
            <div className="flex gap-2">
              <Input data-testid="media-lookup-input" value={lookupName} onChange={(e) => setLookupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runMediaLookup(); } }}
                placeholder="es. Bikeitalia, BiciDaStrada, BikeRadar…" />
              <Button type="button" onClick={runMediaLookup} disabled={lookingUp} data-testid="media-lookup-btn"
                className="shrink-0 rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {lookingUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1.5 h-4 w-4" /> Cerca con AI</>}
              </Button>
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nome testata *</Label>
              <Input data-testid="manual-media-name" value={manualMedia.name} onChange={(e) => setMM({ name: e.target.value })} placeholder="es. Bici.PRO" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Email redazione</Label>
                <Input data-testid="manual-media-email" type="email" value={manualMedia.editor_email} onChange={(e) => setMM({ editor_email: e.target.value })} placeholder="redazione@testata.it" />
              </div>
              <div className="space-y-1.5">
                <Label>Nome redattore</Label>
                <Input data-testid="manual-media-editor" value={manualMedia.editor_name} onChange={(e) => setMM({ editor_name: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Telefono redazione</Label>
                <Input data-testid="manual-media-phone" value={manualMedia.phone} onChange={(e) => setMM({ phone: e.target.value })} placeholder="+39 02 1234567" />
              </div>
              <div className="space-y-1.5">
                <Label>WhatsApp redazione</Label>
                <Input data-testid="manual-media-whatsapp" value={manualMedia.whatsapp} onChange={(e) => setMM({ whatsapp: e.target.value })} placeholder="+39 348 1234567" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Categoria</Label>
                <Select value={manualMedia.category} onValueChange={(v) => setMM({ category: v })}>
                  <SelectTrigger data-testid="manual-media-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nazione</Label>
                <Select value={manualMedia.nation} onValueChange={(v) => setMM({ nation: v })}>
                  <SelectTrigger data-testid="manual-media-nation"><SelectValue /></SelectTrigger>
                  <SelectContent>{NATIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Sito web</Label>
              <Input data-testid="manual-media-website" value={manualMedia.website} onChange={(e) => setMM({ website: e.target.value })} placeholder="www.testata.it" />
            </div>
            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea data-testid="manual-media-notes" value={manualMedia.notes} onChange={(e) => setMM({ notes: e.target.value })} className="min-h-[70px]" placeholder="Info utili sulla testata…" />
            </div>
            <Button onClick={submitManualMedia} disabled={manualSaving} data-testid="manual-media-submit"
              className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {manualSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva testata</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* ===== Discovery box ===== */}
      <div className="mb-6 rounded-2xl border border-border bg-brema-card p-6">
        <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-foreground">
          <Newspaper className="h-5 w-5 text-brand" />
          <h3 className="font-semibold">Ricerca testate</h3>
          <span className="hidden text-xs font-normal text-muted-foreground sm:inline">· trova nuove testate per categoria e nazione</span>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:items-end">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Categoria editoriale</Label>
              <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground" onClick={() => setCatMgr(true)} data-testid="manage-media-category-btn"><Settings2 className="mr-1 h-3.5 w-3.5" /> Gestisci</Button>
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="media-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <FinderOptionsDialog group="media_category" title="Categoria editoriale — Media & PR Finder" open={catMgr} onOpenChange={setCatMgr} onChanged={loadCategories} />
          </div>
          <div className="space-y-1.5">
            <Label>Nazione</Label>
            <Select value={nation} onValueChange={setNation}>
              <SelectTrigger data-testid="media-nation-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {NATIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Quante (max 10)</Label>
            <Input type="number" min={1} max={10} value={count} data-testid="media-count-input"
              onChange={(e) => setCount(e.target.value)} />
          </div>
          <Button onClick={discover} disabled={discovering} data-testid="media-discover-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary font-semibold leading-none text-primary-foreground hover:bg-primary/90">
            {discovering ? <><Loader2 className="h-4 w-4 animate-spin" /> <span className="inline-block translate-y-px leading-none">Ricerca…</span></> : <><Sparkles className="h-4 w-4" /> <span className="inline-block translate-y-px leading-none">Avvia ricerca AI</span></>}
          </Button>
          <Button onClick={() => setDiscoveryOpen(true)} variant="outline" data-testid="ai-discovery-btn"
            className="flex w-full items-center justify-center gap-2 rounded-full border-primary/40 font-semibold leading-none text-primary hover:bg-primary/10">
            <Target className="h-4 w-4" /> AI Discovery Search
          </Button>
        </div>
        <AIDiscoveryDialog open={discoveryOpen} onOpenChange={setDiscoveryOpen}
          title="AI Discovery Search · Media & PR"
          searchEndpoint="/media/discovery-search" archiveEndpoint="/media/discovery-archive"
          searchBody={{ nation, category, count: Number(count) || 8, email_only: false }}
          getKey={(c, i) => c.dedup_key || String(i)}
          getWhy={(c) => c.why}
          renderCandidate={(c) => (
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{c.name}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{c.outlet_type || "Sito"}</span>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">Fit {c.fit_score}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                {[c.category, c.nation].filter(Boolean).join(" · ")}
                {c.website && (<>{" · "}
                  <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`}
                    target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                    data-testid="discovery-website-link" className="text-primary hover:underline">
                    {c.website.replace(/^https?:\/\//, "")}
                  </a>
                </>)}
              </div>
            </div>
          )}
          onArchived={(data) => { loadMedia(); setJustArchived(data?.media || []); }} />
        {discovering && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-brand" data-testid="media-discover-progress">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>
              {discoverProgress?.phase === "searching"
                ? `Ricerca web di testate e riviste${discoverProgress.current_area ? ` · ${discoverProgress.current_area}` : ""}…`
                : discoverProgress?.phase === "compiling"
                ? `L'AI sta selezionando le testate in target${discoverProgress.candidates ? ` · ${discoverProgress.candidates} fonti web` : ""}…`
                : discoverProgress?.phase === "enriching"
                ? "Testate individuate · recupero contatti di redazione in sottofondo…"
                : discoverProgress?.phase === "scanning"
                ? `Scansione a ciclo continuo · pagina ${discoverProgress.page}/${discoverProgress.pages_total} — trovate ${discoverProgress.found ?? 0}/${discoverProgress.target ?? count} con email`
                : "Avvio ricerca AI…"}
            </span>
          </div>
        )}
        <p className="mt-4 text-xs text-muted-foreground">
          {(integrations?.media_mode === "real" || integrations?.brave || integrations?.google_cse) ? (
            <span className="flex items-center gap-1.5 text-positive" data-testid="media-integration-real">
              <Check className="h-3.5 w-3.5" /> Motore LLM-First attivo · l'AI (Claude) + Web Search individuano testate reali per nazione{integrations?.hunter ? " + Hunter.io (email redazioni)" : ""}
            </span>
          ) : (
            <span data-testid="media-integration-demo">
              Modalità dimostrativa: testate generate dall'AI. Aggiungi <code className="text-muted-foreground">BRAVE_SEARCH_API_KEY</code> in <code className="text-muted-foreground">backend/.env</code> per la ricerca reale.
            </span>
          )}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-brema-card">
          <TabsTrigger value="risultati" data-testid="tab-risultati"><Sparkles className="mr-1.5 h-4 w-4" /> Risultati ricerca ({resultsMedia.length})</TabsTrigger>
          <TabsTrigger value="testate" data-testid="tab-testate"><Newspaper className="mr-1.5 h-4 w-4" /> Testate · Archivio ({visibleMedia.length})</TabsTrigger>
          <TabsTrigger value="campagne" data-testid="tab-campagne-pr"><FileText className="mr-1.5 h-4 w-4" /> Bozze PR ({draftBatches.length})</TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico-pr"><Inbox className="mr-1.5 h-4 w-4" /> Storico Invii ({history.length})</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline-pr"><BarChart3 className="mr-1.5 h-4 w-4" /> Pipeline PR · Analytics</TabsTrigger>
        </TabsList>

        {/* ================= TAB RISULTATI RICERCA (temporanei) ================= */}
        <TabsContent value="risultati">
          <div className="mb-4 rounded-2xl border border-brand/30 bg-brema-card p-4" data-testid="media-results-intro">
            <p className="text-sm text-foreground">
              <span className="font-semibold">Risultati temporanei</span> delle ricerche AI e di AI Discovery. Seleziona le testate che ti interessano e usa
              <span className="font-semibold"> "Salva selezionate nell'Archivio Testate"</span>: solo quelle salvate entrano nell'archivio permanente. Puoi generare le bozze PR anche da qui.
            </p>
          </div>
          {resultsMedia.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground" data-testid="media-results-empty">
              Nessun risultato di ricerca in sospeso. Avvia una ricerca AI: le nuove testate compaiono qui.
            </div>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-brema-card p-3" data-testid="media-results-toolbar">
                <Button variant="secondary" size="sm" data-testid="media-results-select-all-btn" onClick={toggleSelectAll}
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
                </Button>
                <span className="text-sm text-foreground" data-testid="media-results-selected-counter">
                  {selectedCount} {selectedCount === 1 ? "testata selezionata" : "testate selezionate"}
                </span>
                <SortSelect options={sortOptions} value={resultsSort} onChange={setResultsSort} testid="media-results-sort-select" className="h-9 w-56 text-xs" />
                <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                  <Button data-testid="media-results-save-btn" disabled={selectedCount === 0 || savingResults} onClick={saveSelectedResults}
                    className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                    {savingResults ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Archive className="mr-2 h-4 w-4" /> Salva selezionate nell'Archivio Testate</>}
                  </Button>
                  <Select value={bulkPitchType} onValueChange={setBulkPitchType}>
                    <SelectTrigger className="h-9 w-44 text-xs" data-testid="media-results-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email di pitch</SelectItem>
                      <SelectItem value="press_release">Comunicato stampa</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="secondary" size="sm" data-testid="media-results-generate-btn" disabled={selectedCount === 0 || bulkRunning} onClick={runBulkGenerate}
                    className="h-9 rounded-full border border-border bg-transparent text-xs font-medium text-foreground hover:bg-brema-hover disabled:opacity-40">
                    {bulkRunning ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Generazione…</> : <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Genera Bozze PR</>}
                  </Button>
                  <Button variant="danger" size="sm" data-testid="media-results-discard-btn" disabled={selectedCount === 0} onClick={() => discardResults(false)}
                    className="h-9 rounded-full text-xs font-medium disabled:opacity-40">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Rimuovi dai risultati
                  </Button>
                  <Button variant="danger" size="sm" data-testid="media-results-clear-btn" onClick={() => discardResults(true)}
                    className="h-9 rounded-full text-xs font-medium">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Svuota risultati
                  </Button>
                </div>
              </div>
              <ErrorBoundary>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="media-results-grid">
                  {resultsMedia.map((m, i) => (
                    <MediaCard key={m?._id || i} m={m} i={i} checked={!!selected[m?._id]} onToggle={toggleSelect} onStatus={changeStatus} onPitch={openPitch} onEdit={setEditMedia} />
                  ))}
                </div>
              </ErrorBoundary>
            </>
          )}
        </TabsContent>

        {/* ================= TAB TESTATE ================= */}
        <TabsContent value="testate">
          {/* Archive filter toolbar */}
          <div className="mb-4 rounded-2xl border border-border bg-brema-card p-5">
            <div className="mb-3 flex items-center gap-2 text-foreground">
              <Archive className="h-4 w-4 text-brand" />
              <h3 className="text-sm font-semibold">Archivio & Gestione Testate</h3>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Categoria</Label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="h-9 w-48" data-testid="media-filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutte">Tutte le categorie</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Nazione</Label>
                <Select value={filterNation} onValueChange={setFilterNation}>
                  <SelectTrigger className="h-9 w-40" data-testid="media-filter-nation"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="tutte">Tutte le nazioni</SelectItem>
                    {NATIONS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Qualità</Label>
                <Select value={minScore} onValueChange={setMinScore}>
                  <SelectTrigger className="h-9 w-40" data-testid="media-filter-quality"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QUALITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Stato</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-9 w-40" data-testid="media-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutti gli stati</SelectItem>
                    {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <SortSelect options={sortOptions} value={mediaSort} onChange={setMediaSort} testid="media-sort-select" label="Ordina per" className="h-9 w-56 text-xs" />
              <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                <Button variant="secondary" size="sm" data-testid="media-check-links-btn" onClick={checkMediaLinks} disabled={checkingLinks}
                  className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  {checkingLinks ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Link2 className="mr-1.5 h-3.5 w-3.5" />} Verifica link
                </Button>
                <Button variant="secondary" size="sm" data-testid="media-dedup-btn" onClick={dedupMedia} disabled={dedupingMedia}
                  className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  {dedupingMedia ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Layers className="mr-1.5 h-3.5 w-3.5" />} Rimuovi duplicati
                </Button>
                <Button variant="secondary" size="sm" data-testid="import-media-btn" onClick={() => setImportOpen(true)}
                  className="h-9 rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" /> Importa CSV/Excel
                </Button>
                <Button size="sm" data-testid="add-manual-media-btn" onClick={() => { setManualMedia(emptyManualMedia); setLookupName(""); setManualOpen(true); }}
                  className="h-9 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Aggiungi testata
                </Button>
                {list.length > 0 && (
                  <Button variant="danger" size="sm" data-testid="clear-media-btn" onClick={clearMedia}
                    className="h-9 rounded-full text-xs">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Svuota
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Bulk selection toolbar */}
          {visibleMedia.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-brema-card p-3" data-testid="media-bulk-toolbar">
              <Button variant="secondary" size="sm" data-testid="media-select-all-btn" onClick={toggleSelectAll}
                className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
              </Button>
              <span className="text-sm text-foreground" data-testid="media-selected-counter">
                {selectedCount} {selectedCount === 1 ? "testata selezionata" : "testate selezionate"}
              </span>
              <ChannelToggle value={bulkChannel} onChange={setBulkChannel} testidPrefix="media-bulk" />
              {bulkChannel === "whatsapp" && (
                <Button variant="secondary" size="sm" data-testid="media-select-wa-btn" onClick={selectOnlyWithWa}
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  Solo con WhatsApp ({waCoverage})
                </Button>
              )}
              {bulkChannel === "email" && (
                <Input value={bulkNews} onChange={(e) => setBulkNews(e.target.value)} data-testid="media-bulk-news"
                  placeholder="Novità PR (opzionale, es. collezione SS26)…" className="h-9 w-64 bg-brema-bg" />
              )}
              <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
                {bulkChannel === "email" ? (
                  <>
                    <Select value={bulkPitchType} onValueChange={setBulkPitchType}>
                      <SelectTrigger className="h-9 w-44 text-xs" data-testid="media-bulk-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email di pitch</SelectItem>
                        <SelectItem value="press_release">Comunicato stampa</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button data-testid="media-bulk-generate-btn" disabled={selectedCount === 0 || bulkRunning} onClick={runBulkGenerate}
                      className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                      {bulkRunning ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generazione…</> : <><Sparkles className="mr-2 h-4 w-4" /> Genera Bozze PR</>}
                    </Button>
                    <Button variant="secondary" data-testid="media-bulk-email-unique-btn" disabled={selectedCount === 0} onClick={() => setBulkComposerOpen(true)}
                      className="rounded-full border border-border bg-transparent text-xs font-semibold text-foreground hover:bg-brema-hover disabled:opacity-40">
                      <Mail className="mr-1.5 h-3.5 w-3.5" /> Messaggio unico / Modello
                    </Button>
                  </>
                ) : (
                  <Button data-testid="media-bulk-whatsapp-btn" disabled={selectedCount === 0} onClick={() => setBulkComposerOpen(true)}
                    className="rounded-full bg-[#25D366] font-semibold text-white hover:bg-[#1FB855] disabled:opacity-40">
                    <MessageCircle className="mr-2 h-4 w-4" /> Invia WhatsApp ai selezionati ({selectedCount})
                  </Button>
                )}
                <Button variant="danger" size="sm" data-testid="media-delete-selected-btn" disabled={selectedCount === 0} onClick={deleteSelectedMedia}
                  className="h-9 rounded-full text-xs font-medium disabled:opacity-40">
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Elimina selezionate
                </Button>
              </div>
            </div>
          )}

          {visibleMedia.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">
              {list.length === 0
                ? "Nessuna testata in archivio. Avvia una ricerca AI e salva i risultati, oppure importa un file CSV/Excel."
                : (archiveMedia.length === 0
                  ? `Archivio vuoto: hai ${resultsMedia.length} risultati di ricerca in sospeso nella tab "Risultati ricerca".`
                  : "Nessuna testata per i filtri selezionati.")}
            </div>
          ) : (
            <ErrorBoundary>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {visibleMedia.map((m, i) => (
                  <MediaCard key={m?._id || i} m={m} i={i} checked={!!selected[m?._id]} onToggle={toggleSelect} onStatus={changeStatus} onPitch={openPitch} onEdit={setEditMedia} />
                ))}
              </div>
            </ErrorBoundary>
          )}
        </TabsContent>

        {/* ================= TAB CAMPAGNE PR ================= */}
        <TabsContent value="campagne">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="flex items-center gap-1.5 text-sm font-medium text-foreground" data-testid="pr-drafts-label"><FileText className="h-4 w-4 text-brand" /> Bozze PR · lavori in corso, non ancora inviati</p>
              <p className="text-xs text-muted-foreground">Qui trovi solo le bozze salvate (pitch singoli, campagne generate, bozze libere). Le campagne spedite sono nello <button type="button" className="underline hover:text-brand" onClick={() => setTab("storico")} data-testid="pr-goto-storico">Storico Invii</button>; le metriche nella <button type="button" className="underline hover:text-brand" onClick={() => setTab("pipeline")}>Pipeline PR</button>.</p>
            </div>
            <Button data-testid="new-master-campaign-btn" onClick={openMaster}
              className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              <FilePlus2 className="mr-2 h-4 w-4" /> Nuova Campagna PR / Scrivi Comunicato
            </Button>
          </div>
          {draftBatches.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">
              Nessuna bozza PR. Usa "Nuova Campagna PR" per scrivere una bozza libera, oppure seleziona delle testate e usa "Genera Bozze PR" o "Pitch".
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {draftBatches.map((b) => (
                <div key={b._id} data-testid="pr-batch-card" onClick={() => openBatch(b)}
                  className="cursor-pointer rounded-xl border border-border bg-brema-card p-5 transition-colors hover:border-primary/40">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${batchStatusClass(b.status)}`}>{batchStatusLabel(b.status)}</span>
                      {b.kind === "master" && <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-medium text-brand" data-testid="pr-batch-master-badge">Master</span>}
                    </div>
                    <button data-testid="pr-batch-delete-btn" onClick={(e) => deleteBatch(b, e)}
                      className="text-muted-foreground hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <p className="text-xs text-muted-foreground">{fmtDateTime(b.created_at)}</p>
                  <h4 className="mt-1 font-semibold text-foreground">{b.title}</h4>
                  {b.sample_subject && <p className="mt-1 line-clamp-2 text-sm text-foreground">Oggetto: {b.sample_subject}</p>}
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-foreground">
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5 text-brand" /> {b.recipient_count} testate</span>
                    <span className="text-muted-foreground">·</span>
                    <span>{b.sent_count || 0} inviate</span>
                    {(b.attachments || []).length > 0 && (
                      <span className="flex items-center gap-1 text-muted-foreground"><Paperclip className="h-3.5 w-3.5" /> {b.attachments.length}</span>
                    )}
                  </div>
                  <Button size="sm" data-testid="open-pr-batch-btn"
                    className="mt-4 w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                    Apri e gestisci
                  </Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ================= TAB STORICO INVII ================= */}
        <TabsContent value="storico">
          <p className="mb-3 flex items-center gap-1.5 text-sm font-medium text-foreground" data-testid="pr-history-label"><Inbox className="h-4 w-4 text-brand" /> Storico Invii · campagne e pitch effettivamente spediti (registro)</p>
          {history.length > 0 && <EmailFunnel history={history} />}
          {history.length === 0 ? (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button size="sm" variant="secondary" data-testid="pr-clean-false-sends-btn-empty" onClick={cleanFalseSends} disabled={cleaningFalse}
                  title="Ripristina i pitch segnati 'Inviata' ma senza un destinatario reale"
                  className="h-8 rounded-full border border-amber-500/40 bg-transparent text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-500/10">
                  {cleaningFalse ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="mr-1 h-3.5 w-3.5" />} Pulisci invii falsi
                </Button>
              </div>
              <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground">
                Nessun invio PR. I pitch e i comunicati spediti compariranno qui come registro.
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground" data-testid="pr-history-selected-count">{historySel.size} selezionati</span>
                {historySel.size > 0 && (
                  <Button size="sm" variant="danger" data-testid="pr-history-delete-selected-btn" onClick={() => setHistDelOpen(true)}
                    className="h-8 rounded-full text-xs">
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Elimina selezionati ({historySel.size})
                  </Button>
                )}
                <Button size="sm" variant="secondary" data-testid="pr-clean-false-sends-btn" onClick={cleanFalseSends} disabled={cleaningFalse}
                  title="Ripristina i pitch segnati 'Inviata' ma senza un destinatario reale"
                  className="ml-auto h-8 rounded-full border border-amber-500/40 bg-transparent text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-500/10">
                  {cleaningFalse ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="mr-1 h-3.5 w-3.5" />} Pulisci invii falsi
                </Button>
              </div>
              <div className="overflow-x-auto rounded-xl border border-border bg-brema-card" data-testid="pr-history-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="w-10 px-4 py-3">
                      <Checkbox checked={history.length > 0 && historySel.size === history.length} onCheckedChange={toggleHistAll} data-testid="pr-history-select-all" />
                    </th>
                    <th className="px-4 py-3 font-medium">Data invio</th>
                    <th className="px-4 py-3 font-medium">Testata</th>
                    <th className="px-4 py-3 font-medium">Email / WhatsApp</th>
                    <th className="px-4 py-3 font-medium">Oggetto</th>
                    <th className="px-4 py-3 font-medium">Allegati</th>
                    <th className="px-4 py-3 font-medium">Stato consegna</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((m) => (
                    <tr key={m._id} data-testid="pr-history-row" onClick={() => setHistoryDetail(m)}
                      className="cursor-pointer border-b border-border text-foreground last:border-0 hover:bg-muted">
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={historySel.has(m._id)} onCheckedChange={() => toggleHistSel(m._id)} data-testid="pr-history-row-checkbox" />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDateTime(m.sent_at)}</td>
                      <td className="px-4 py-3 font-medium text-foreground">
                        <div data-testid="pr-history-outlet">{m.media_name || m.lead_name || "—"}</div>
                        {m.contact_website && (
                          <a href={/^https?:\/\//i.test(m.contact_website) ? m.contact_website : `https://${m.contact_website}`} target="_blank" rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()} data-testid="pr-history-site"
                            className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-normal text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-brand">
                            <Globe className="h-3 w-3" /> {m.contact_website.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "")}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5">
                          {m.channel === "whatsapp" && <span className="inline-flex items-center gap-0.5 rounded-full bg-[#25D366]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#128C7E]" data-testid="pr-history-channel-wa"><MessageCircle className="h-3 w-3" /> WA</span>}
                          {m.recipient || "—"}
                        </span>
                      </td>
                      <td className="max-w-[280px] truncate px-4 py-3 text-foreground">{m.subject || (m.channel === "whatsapp" ? (m.email_body || "Messaggio WhatsApp") : "—")}</td>
                      <td className="px-4 py-3 text-muted-foreground">{(m.attachments || []).length ? <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" /> {m.attachments.length}</span> : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${deliveryClass(m.delivery_status || "inviata")}`}>
                          {DELIVERY_LABELS[m.delivery_status] || "Inviata"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ================= TAB PIPELINE PR (Cruscotto) ================= */}
        <TabsContent value="pipeline">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3" data-testid="pipeline-header">
            <div>
              <p className="flex items-center gap-1.5 text-sm font-medium text-foreground" data-testid="pipeline-label"><Gauge className="h-4 w-4 text-brand" /> Dashboard di Monitoraggio e Analytics PR</p>
              <p className="text-xs text-muted-foreground">Funnel di conversione dei pitch inviati (Invii → Consegnate → Aperte → Cliccate → Risposte), aggiornato dagli eventi del provider email. Usa i controlli di manutenzione per rimuovere prove interne e invii di test che falserebbero le metriche.</p>
            </div>
            {pipeline && pipeline.totals.sent > 0 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="pipeline-maintenance">
                {pipeSel.size > 0 && (
                  <Button size="sm" variant="danger" data-testid="pipeline-remove-selected-btn" className="h-8 rounded-full text-xs"
                    onClick={() => { setPipeDeleteHistory(false); setPipeRemove({ ids: [...pipeSel], mode: "selected" }); }}>
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Rimuovi selezionati ({pipeSel.size})
                  </Button>
                )}
                {(pipeline.totals.simulated || 0) > 0 && (
                  <Button size="sm" variant="secondary" data-testid="pipeline-reset-simulated-btn"
                    onClick={() => { setPipeDeleteHistory(false); setPipeRemove({ ids: [], mode: "simulated" }); }}
                    className="h-8 rounded-full border border-amber-500/40 bg-transparent text-xs text-amber-700 hover:bg-amber-500/10 dark:text-amber-300">
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset simulazioni di prova ({pipeline.totals.simulated})
                  </Button>
                )}
              </div>
            )}
          </div>
          {!pipeline || pipeline.totals.sent === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-brema-card/50 p-12 text-center text-muted-foreground" data-testid="pipeline-empty">
              Nessun pitch inviato ancora. Invia comunicati alle testate: qui vedrai invii → aperture → risposte.
            </div>
          ) : (
            <div className="space-y-6" data-testid="pr-pipeline">
              {/* KPI cards */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {[
                  { key: "sent", label: "Invii", val: pipeline.totals.sent, sub: `su ${pipeline.totals.outlets} testate`, Icon: Send, color: "text-sky-500" },
                  { key: "delivered", label: "Consegnate", val: pipeline.totals.delivered, sub: `${pipeline.rates.delivery_rate}%`, Icon: CheckCircle2, color: "text-blue-500" },
                  { key: "opened", label: "Aperte", val: pipeline.totals.opened, sub: `${pipeline.rates.open_rate}% aperture`, Icon: MailOpen, color: "text-positive" },
                  { key: "clicked", label: "Cliccate", val: pipeline.totals.clicked, sub: `${pipeline.rates.click_rate}%`, Icon: MousePointerClick, color: "text-amber-500" },
                  { key: "replied", label: "Risposte", val: pipeline.totals.replied, sub: `${pipeline.rates.reply_rate}% risposte`, Icon: Reply, color: "text-emerald-500" },
                ].map((c) => (
                  <div key={c.key} data-testid={`pipeline-kpi-${c.key}`} className="rounded-xl border border-border bg-brema-card p-4">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</span>
                      <c.Icon className={`h-4 w-4 ${c.color}`} />
                    </div>
                    <p className="text-2xl font-bold text-foreground">{c.val}</p>
                    <p className={`text-xs ${c.color}`}>{c.sub}</p>
                  </div>
                ))}
              </div>

              {/* Funnel bars */}
              <div className="rounded-xl border border-border bg-brema-card p-6">
                <div className="mb-4 flex items-center gap-2 text-foreground">
                  <TrendingUp className="h-5 w-5 text-brand" />
                  <h3 className="font-semibold">Funnel di conversione PR</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { label: "Invii", val: pipeline.totals.sent, color: "bg-sky-500" },
                    { label: "Consegnate", val: pipeline.totals.delivered, color: "bg-blue-500" },
                    { label: "Aperte", val: pipeline.totals.opened, color: "bg-positive" },
                    { label: "Cliccate", val: pipeline.totals.clicked, color: "bg-amber-500" },
                    { label: "Risposte", val: pipeline.totals.replied, color: "bg-emerald-500" },
                  ].map((s) => {
                    const pct = pipeline.totals.sent > 0 ? Math.round((s.val / pipeline.totals.sent) * 100) : 0;
                    return (
                      <div key={s.label} className="flex items-center gap-3">
                        <span className="w-24 shrink-0 text-sm text-muted-foreground">{s.label}</span>
                        <div className="h-6 flex-1 overflow-hidden rounded-full bg-brema-bg">
                          <div className={`flex h-full items-center justify-end rounded-full ${s.color} px-2 transition-all`} style={{ width: `${Math.max(pct, s.val > 0 ? 8 : 0)}%` }}>
                            {s.val > 0 && <span className="text-[11px] font-semibold text-white">{s.val}</span>}
                          </div>
                        </div>
                        <span className="w-12 shrink-0 text-right text-sm font-medium text-foreground">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
                {pipeline.totals.bounced > 0 && (
                  <p className="mt-4 text-xs text-red-500" data-testid="pipeline-bounced">
                    ⚠ {pipeline.totals.bounced} pitch rimbalzati / segnalati come spam.
                  </p>
                )}
              </div>

              {/* Per-outlet table */}
              <div className="overflow-x-auto rounded-xl border border-border bg-brema-card" data-testid="pipeline-outlets-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <th className="w-10 px-4 py-3"><Checkbox checked={pipeline.outlets.length > 0 && pipeSel.size === pipeline.outlets.length} onCheckedChange={() => setPipeSel((s) => (s.size === pipeline.outlets.length ? new Set() : new Set(pipeline.outlets.map((o) => o.id))))} data-testid="pipeline-select-all" /></th>
                      <th className="px-4 py-3 font-medium">Testata</th>
                      <th className="px-4 py-3 font-medium">Tipo</th>
                      <th className="px-4 py-3 font-medium">Nazione</th>
                      <th className="px-4 py-3 font-medium">Stato pitch</th>
                      <th className="px-4 py-3 font-medium">Destinatario</th>
                      <th className="px-4 py-3 font-medium">Ultimo evento</th>
                      <th className="w-10 px-2 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pipeline.outlets.map((o) => (
                      <tr key={o.id} data-testid="pipeline-outlet-row" className="border-b border-border last:border-0 hover:bg-muted">
                        <td className="px-4 py-3">
                          <Checkbox checked={pipeSel.has(o.id)} onCheckedChange={() => togglePipeSel(o.id)} data-testid="pipeline-row-checkbox" />
                        </td>
                        <td className="px-4 py-3 font-medium text-foreground">{o.name || "—"}{o.simulated && <span className="ml-2 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300" data-testid="pipeline-simulated-badge">Prova simulata</span>}</td>
                        <td className="px-4 py-3 text-muted-foreground">{o.outlet_type || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{o.nation || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${deliveryClass(o.pitch_delivery_status)}`}>
                            {DELIVERY_LABELS[o.pitch_delivery_status] || "Inviata"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{o.pitch_recipient || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDateTime(o.pitch_delivery_updated_at || o.pitch_sent_at)}</td>
                        <td className="px-2 py-3">
                          <button type="button" data-testid="pipeline-row-remove-btn" title="Rimuovi dalla Pipeline (azzera le metriche di questa testata)"
                            onClick={() => { setPipeDeleteHistory(false); setPipeRemove({ ids: [o.id], mode: "selected", name: o.name }); }}
                            className="text-muted-foreground hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ===== Quick pitch dialog (unified composer) ===== */}
      <EmailComposer
        open={!!dialogMedia}
        onOpenChange={(o) => !o && setDialogMedia(null)}
        target="media"
        contact={dialogMedia}
        templateModule="redazionali"
        defaultTo={dialogMedia?.editor_email || ""}
        showPitchType
        title={`PR · ${dialogMedia?.name || ""}`}
        channelHint="Genera un'email di pitch o un comunicato stampa (o scrivi da zero), aggiungi CC/CCN e allegati dal Catalogo, poi invia alla redazione."
        onSent={() => { loadMedia(); loadBatches(); loadHistory(); }}
      />

      {/* ===== Master compose dialog ===== */}
      <Dialog open={masterOpen} onOpenChange={setMasterOpen}>
        <DialogContent className="max-w-lg border-border bg-brema-card">
          <DialogHeader>
            <DialogTitle className="text-foreground">Nuova Campagna PR · Bozza libera</DialogTitle>
            <DialogDescription>Scrivi il testo (o generalo con l'AI), scegli la lingua e allega i materiali. Salva la bozza e scegli i destinatari dopo.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo di contenuto</Label>
                <Select value={masterType} onValueChange={setMasterType}>
                  <SelectTrigger data-testid="master-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email di pitch</SelectItem>
                    <SelectItem value="press_release">Comunicato stampa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1"><Languages className="h-3.5 w-3.5" /> Lingua</Label>
                <Select value={masterLang} onValueChange={setMasterLang}>
                  <SelectTrigger data-testid="master-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Novità del brand (per la generazione AI · opzionale)</Label>
              <div className="flex gap-2">
                <Input value={masterNews} onChange={(e) => setMasterNews(e.target.value)} data-testid="master-news"
                  placeholder="Es. Collezione gravel SS26…" className="flex-1" />
                <Button type="button" variant="secondary" onClick={generateMaster} disabled={masterGenerating} data-testid="master-generate-btn"
                  className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                  {masterGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="mr-1.5 h-3.5 w-3.5" /> Genera con AI</>}
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Oggetto</Label>
              <Input value={masterSubject} onChange={(e) => setMasterSubject(e.target.value)} data-testid="master-subject-input"
                placeholder="Oggetto del comunicato / email" />
            </div>
            <div className="space-y-1.5">
              <Label>Testo</Label>
              <Textarea value={masterBody} onChange={(e) => setMasterBody(e.target.value)} data-testid="master-body-textarea"
                className="min-h-[200px]" placeholder="Scrivi qui il testo, oppure generalo con l'AI…" />
              <TranslateControls text={masterBody} subject={masterSubject} lang={masterLang} showSelect={false}
                onApply={({ text, subject: s }) => { setMasterBody(text); if (s !== undefined) setMasterSubject(s); }}
                testidPrefix="master-translate" label={`Traduci oggetto e testo in ${masterLang}`} />
            </div>
            <EmailPreviewButton body={masterBody} subject={masterSubject} />
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" /> Allegati (PDF, immagini, Office)</Label>
              <AttachmentUploader attachments={masterAtts} onChange={setMasterAtts} testidPrefix="master-att" />
            </div>
            <Button onClick={saveMaster} disabled={masterSaving} data-testid="master-save-btn"
              className="w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              {masterSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio…</> : <><Save className="mr-2 h-4 w-4" /> Salva bozza master</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Batch detail dialog ===== */}
      <Dialog open={!!activeBatch} onOpenChange={(o) => !o && setActiveBatch(null)}>
        <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col overflow-hidden border-border bg-brema-card" data-testid="pr-batch-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              {activeBatch?.title}
              {activeBatch && (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${batchStatusClass(activeBatch.status)}`} data-testid="pr-batch-status-badge">
                  {activeBatch.status === "inviata" ? "Inviata" : "Bozza"}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {activeBatch?.recipient_count} testate · {activeBatch?.sent_count || 0} inviate · creata il {fmtDateTime(activeBatch?.created_at)}
              {activeBatch?.status !== "inviata" && <> · destinatari ed email sono modificabili qui sotto (salva con "Salva")</>}
            </DialogDescription>
          </DialogHeader>
          {batchLoading ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto pr-1" data-testid="pr-batch-scroll">
              <div className="mb-3 rounded-lg border border-border bg-brema-bg p-4">
                <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-foreground">
                  <Paperclip className="h-4 w-4 text-brand" /> Allegati della campagna
                  <span className="text-xs font-normal text-muted-foreground">· inviati a tutte le testate del batch</span>
                </p>
                <AttachmentUploader attachments={batchAttachments} onChange={persistBatchAttachments} testidPrefix="batch-att" />
              </div>

              <div className="mb-3">
                <BatchCcBcc batch={activeBatch} testidPrefix="pr-batch" />
              </div>

              {/* Master: recipient picker */}
              {isMaster && (
                <div className="mb-3 rounded-lg border border-border bg-brema-bg p-4" data-testid="recipient-picker">
                  <p className="mb-2 flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <Users className="h-4 w-4 text-brand" /> Destinatari
                    <span className="text-xs font-normal text-muted-foreground">· scegli a quali testate inviare questa bozza</span>
                  </p>
                  {availableRecipients.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Tutte le testate in archivio sono già state aggiunte. Cerca nuove testate dalla tab Testate.</p>
                  ) : (
                    <>
                      <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-border bg-brema-card p-2">
                        {availableRecipients.map((m) => (
                          <label key={m._id} data-testid="recipient-option"
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm text-foreground hover:bg-brema-hover">
                            <Checkbox checked={!!recipientSel[m._id]}
                              onCheckedChange={() => setRecipientSel((s) => ({ ...s, [m._id]: !s[m._id] }))}
                              data-testid="recipient-checkbox" className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                            <span className="truncate">{m.name}</span>
                            <span className="text-xs text-muted-foreground">{m.nation}{m.editor_email ? "" : " · no email"}</span>
                          </label>
                        ))}
                      </div>
                      <Button size="sm" disabled={recipientSelCount === 0 || addingRecipients} onClick={addRecipients} data-testid="add-recipients-btn"
                        className="mt-2 rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40">
                        {addingRecipients ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Aggiunta…</> : <><Users className="mr-1.5 h-3.5 w-3.5" /> Aggiungi destinatari selezionati ({recipientSelCount})</>}
                      </Button>
                    </>
                  )}
                </div>
              )}

              {!isMaster && prTemplates.length > 0 && batchMessages.length > 0 && (
                <div className="mb-3 space-y-1.5 rounded-lg border border-border bg-brema-bg p-3" data-testid="pr-template-block">
                  <Label className="text-[11px] font-semibold text-muted-foreground">Carica da Archivio Modelli (Moduli AI: testo + allegato PDF)</Label>
                  <Select onValueChange={applyPrTemplate}>
                    <SelectTrigger data-testid="pr-template-select"><SelectValue placeholder="Applica un modello a tutte le testate…" /></SelectTrigger>
                    <SelectContent>{prTemplates.map((t) => <SelectItem key={t._id} value={t._id}>{t.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {batchMessages.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                  {isMaster ? "Nessun destinatario ancora. Selezionane sopra e aggiungili." : "Nessuna bozza in questa campagna."}
                </div>
              ) : (
                <div className="space-y-3" data-testid="pr-batch-messages">
                  {batchMessages.map((m, idx) => {
                    const sent = m.status === "inviata";
                    return (
                      <div key={m._id} data-testid="pr-batch-message-row" className="rounded-lg border border-border bg-brema-bg p-4">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            {!sent && (
                              <Checkbox checked={!!msgSel[m._id]} onCheckedChange={() => setMsgSel((s) => ({ ...s, [m._id]: !s[m._id] }))}
                                data-testid="pr-msg-checkbox" className="border-border data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" />
                            )}
                            <span className="font-medium text-foreground">{m.media_name || m.lead_name}</span>
                            {sent && m.recipient && <span className="text-xs text-muted-foreground">{m.recipient}</span>}
                            {!sent && m.media_id && (
                              <button type="button" data-testid="pr-msg-edit-outlet-btn" title="Modifica scheda testata (URL, email, referenti)"
                                onClick={() => { const o = list.find((x) => x._id === m.media_id); if (o) setEditMedia(o); else toast.error("Testata non trovata in archivio"); }}
                                className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground hover:border-brand hover:text-brand">
                                <Pencil className="h-3 w-3" /> Modifica testata
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {sent
                              ? <span className="rounded-full bg-positive/15 px-2 py-0.5 text-[11px] font-medium text-positive" data-testid="pr-msg-sent-badge">Inviata ✓</span>
                              : <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-foreground">Bozza</span>}
                            {!sent && (
                              <button type="button" onClick={() => deleteMsg(m)} data-testid="pr-msg-delete-btn"
                                title="Elimina bozza" className="text-muted-foreground hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                            )}
                          </div>
                        </div>
                        {!sent && (
                          <>
                            <Label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Destinatario (email, separa più indirizzi con virgola):</Label>
                            <Input value={m.recipient || ""} data-testid="pr-msg-recipient-input"
                              onChange={(e) => updateMsgField(idx, "recipient", e.target.value)}
                              className="mb-2" placeholder="redazione@testata.it" />
                          </>
                        )}
                        <Label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Oggetto Email:</Label>
                        <Input value={m.subject || ""} data-testid="pr-msg-subject-input" disabled={sent}
                          onChange={(e) => updateMsgField(idx, "subject", e.target.value)}
                          className="mb-2 disabled:opacity-70" placeholder="Scrivi qui l'oggetto dell'email" />
                        <Textarea value={m.email_body || ""} data-testid="pr-msg-body-textarea" disabled={sent}
                          onChange={(e) => updateMsgField(idx, "email_body", e.target.value)}
                          className="min-h-[120px] disabled:opacity-70" placeholder="Testo" />
                        <AttachmentChips attachments={batchAttachments}
                          onRemove={sent ? null : (sp) => persistBatchAttachments(batchAttachments.filter((a) => a.storage_path !== sp))} />
                        {!sent && (
                          <div className="mt-2 flex justify-end gap-2">
                            <Button size="sm" variant="secondary" data-testid="pr-msg-save-btn" onClick={() => saveMsg(m)}
                              className="rounded-full border border-border bg-transparent text-xs text-foreground hover:bg-brema-hover">
                              <Save className="mr-1 h-3.5 w-3.5" /> Salva
                            </Button>
                            <Button size="sm" data-testid="pr-msg-send-btn" onClick={() => sendMsg(m, idx)}
                              className="rounded-full bg-primary text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                              <Send className="mr-1 h-3.5 w-3.5" /> Invia
                            </Button>
                          </div>
                        )}
                        {!m.recipient && !sent && <p className="mt-1 text-[11px] font-medium text-red-600 dark:text-red-400">Nessuna email di redazione: l'invio sarà simulato.</p>}
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
              {batchMessages.some((m) => m.status !== "inviata") && (
                <div className="mt-2 flex flex-wrap justify-end gap-2 border-t border-border pt-3">
                  <Button variant="secondary" disabled={draftSelCount === 0 || batchSending} onClick={sendSelected} data-testid="send-selected-pr-btn"
                    className="rounded-full border border-border bg-transparent font-semibold text-foreground hover:bg-brema-hover disabled:opacity-40">
                    <Send className="mr-2 h-4 w-4" /> Invia selezionate ({draftSelCount})
                  </Button>
                  <Button onClick={sendWholeBatch} disabled={batchSending} data-testid="send-pr-batch-btn"
                    className="rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                    {batchSending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio…</> : <><Megaphone className="mr-2 h-4 w-4" /> Invia tutte le bozze</>}
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== History detail dialog ===== */}
      <Dialog open={!!historyDetail} onOpenChange={(o) => !o && setHistoryDetail(null)}>
        <DialogContent className="max-w-lg border-border bg-brema-card" data-testid="pr-history-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{historyDetail?.media_name || historyDetail?.lead_name || "Invio PR"}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-2">
              {historyDetail?.contact_website && (
                <a href={/^https?:\/\//i.test(historyDetail.contact_website) ? historyDetail.contact_website : `https://${historyDetail.contact_website}`} target="_blank" rel="noopener noreferrer"
                  data-testid="history-detail-site" className="inline-flex items-center gap-1 font-medium text-brand underline decoration-dotted underline-offset-2">
                  <Globe className="h-3.5 w-3.5" /> {historyDetail.contact_website.replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/$/, "")}
                </a>
              )}
              {historyDetail?.contact_website && <span>·</span>}
              <span className="whitespace-nowrap">{fmtDateTime(historyDetail?.sent_at)}</span>
              {historyDetail?.recipient && <><span>·</span><a href={`mailto:${historyDetail.recipient}`} className="hover:text-brand">{historyDetail.recipient}</a></>}
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${deliveryClass(historyDetail?.delivery_status || "inviata")}`}>
                {DELIVERY_LABELS[historyDetail?.delivery_status] || "Inviata"}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Oggetto</p>
              <p className="text-sm font-medium text-foreground" data-testid="history-detail-subject">{historyDetail?.subject || "—"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Testo inviato</p>
              <MessageBody text={historyDetail?.email_body || ""} className="rounded-lg bg-brema-bg p-3" testId="history-detail-body" />
            </div>
            {(historyDetail?.attachments || []).length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Allegati inclusi</p>
                <AttachmentChips attachments={historyDetail.attachments} testid="history-detail-att" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="danger" data-testid="history-detail-delete-btn"
              onClick={() => { if (historyDetail?._id) { setHistorySel(new Set([historyDetail._id])); setHistDelOpen(true); } }}
              className="rounded-full">
              <Trash2 className="mr-1.5 h-4 w-4" /> Elimina dallo storico
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={histDelOpen} onOpenChange={setHistDelOpen}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="pr-history-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">Elimina dallo storico</DialogTitle>
            <DialogDescription>
              Vuoi eliminare <strong className="text-foreground">{historySel.size}</strong> {historySel.size === 1 ? "invio" : "invii"} dallo Storico? L'azione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setHistDelOpen(false)}
              className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button data-testid="pr-history-delete-confirm-btn" disabled={deletingHist} onClick={deleteHistorySelected}
              className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">
              {deletingHist ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Elimina</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Modifica scheda testata ===== */}
      <MediaEditDialog media={editMedia} open={!!editMedia} onOpenChange={(o) => !o && setEditMedia(null)}
        categories={categories} nations={NATIONS} onSaved={onMediaSaved} />

      {/* ===== Invio massivo Email/WhatsApp (stesso motore dei Rivenditori) ===== */}
      <BulkCampaignComposer open={bulkComposerOpen} onOpenChange={setBulkComposerOpen} channel={bulkChannel} target="media"
        recipients={bulkRecipients} integrations={integrations} testidPrefix="media-bulk"
        onSent={() => { setSelected({}); setTab("storico"); loadBatches(); loadHistory(); loadMedia(); }} />

      {/* ===== Pipeline PR: conferma manutenzione dati ===== */}
      <Dialog open={!!pipeRemove} onOpenChange={(o) => !o && setPipeRemove(null)}>
        <DialogContent className="max-w-md border-border bg-brema-card" data-testid="pipeline-remove-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground">{pipeRemove?.mode === "simulated" ? "Reset simulazioni di prova" : "Rimuovi dalla Pipeline PR"}</DialogTitle>
            <DialogDescription>
              {pipeRemove?.mode === "simulated"
                ? <>Azzera le metriche di tutte le prove <strong className="text-foreground">simulate</strong> (invii senza un ID reale del provider email). Le testate restano in archivio.</>
                : <>Azzera le metriche pitch di <strong className="text-foreground">{pipeRemove?.name || `${pipeRemove?.ids?.length || 0} ${pipeRemove?.ids?.length === 1 ? "testata" : "testate"}`}</strong>: la testata resta in archivio (stato riportato a "nuovo" se era "contattato") e sparisce da funnel e KPI.</>}
            </DialogDescription>
          </DialogHeader>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground" data-testid="pipeline-remove-history-toggle">
            <Checkbox checked={pipeDeleteHistory} onCheckedChange={(v) => setPipeDeleteHistory(!!v)} data-testid="pipeline-remove-history-checkbox" />
            Elimina anche i relativi invii dallo Storico Invii{pipeRemove?.mode === "simulated" ? " (solo gli invii simulati di queste testate)" : ""}
          </label>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setPipeRemove(null)}
              className="rounded-full border border-border bg-transparent text-foreground hover:bg-brema-hover">Annulla</Button>
            <Button data-testid="pipeline-remove-confirm-btn" disabled={pipeWorking} onClick={confirmPipeRemove}
              className="rounded-full bg-brema-red font-semibold text-white hover:bg-brema-red/90">
              {pipeWorking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Conferma</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
